import { FormSectionMeta } from '@people-science/forms-core';
import {
  State,
  StateMachine,
  StateNode,
  SubscriptionCallback,
  SubscriptionType
} from '@people-science/state-machine';
import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';
import Colors from '../constants/Colors';
import {
  DynamicFormContextState,
  useDynamicFormContext
} from './DynamicFormContext';
import FormSection from './FormSection';
import { Platform } from 'react-native';
import * as Device from 'expo-device';
import ProgressBar from '../components/ProgressBar';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeTabBarHeight } from '../common/safeTabBarHeight';
import {
  hasUnsavedAnswers,
  previousSectionPath,
  SectionStep
} from './navigation/formBack';
import { elementsActOnMount } from './drafts/resumableStatePath';

const StyledDynamicFormView = styled.View`
  flex: 1;
`;

const StyledProgressView = styled.View`
  width: 100%;
  flex-direction: row;
`;

const StyledProgressContainerView = styled.View`
  flex: 1;
`;

// const StyledProgressText = styled(Caption)`
//   margin-left: 12px;
// `;

const StyledScrollView = styled(KeyboardAwareScrollView)`
  width: 100%;
  height: 100%;
  background: ${Colors.background};
  padding-horizontal: 15px;
`;

/** Replace the machine's answers in place; skipped when both are the same object, as on mount. */
export function syncMachineFormValues(
  machineValues: Record<string, unknown> | undefined,
  values: Record<string, unknown> | undefined
): void {
  if (!machineValues || !values || machineValues === values) return;
  Object.keys(machineValues).forEach(key => delete machineValues[key]);
  Object.assign(machineValues, values);
}

export interface DynamicFormBackApi {
  goBack: () => SectionStep | undefined;
  syncToPath: (path?: string) => void;
  hasUnsavedAnswers: () => boolean;
}

export interface FormProps<FormValuesType, DataSourceContext = any> {
  statePath?: string;
  restoredHistory?: string[];
  savedValues?: Partial<FormValuesType>;
  backApiRef?: React.MutableRefObject<DynamicFormBackApi | undefined>;
  values?: FormValuesType;
  appendSectionControls?: JSX.Element;
  dataSourceContext?: DataSourceContext;
  onInit?: (formContext: DynamicFormContextState<FormValuesType, any>) => void;
  onChange?: (values: Partial<FormValuesType>) => void;
  onBack?: () => void;
  onStatePathChange?: (
    statePath: string,
    stateNodes: StateNode<FormSectionMeta<FormValuesType, any>, any>[],
    sectionIndex: number,
    sectionCount: number,
    stateMachine: StateMachine<DynamicFormContextState<FormValuesType, any>>
  ) => void;
}

const DynamicForm = <
  FormValuesType extends object,
  FormElementConfigType extends object
>({
  statePath,
  restoredHistory,
  savedValues,
  backApiRef,
  values,
  appendSectionControls,
  dataSourceContext,
  children,
  onInit,
  onChange,
  onBack,
  onStatePathChange
}: PropsWithChildren<FormProps<FormValuesType>>) => {
  const formContext = useDynamicFormContext<
    FormValuesType,
    FormElementConfigType
  >();
  const {
    stateMachine,
    activeStateNodes,
    setActiveStateNodes,
    formValues,
    setFormValues,
    transition,
    isScrollEnabled,
    elementPlugins
  } = formContext;
  const previousFormValues = useRef(formValues);
  const previousStatePath = useRef(statePath);
  const mountRef = useRef(false);
  const onInitRef = useRef(onInit);
  const onChangeRef = useRef(onChange);
  const onStatePathChangeRef = useRef(onStatePathChange);
  const dataSourcecContextRef = useRef(dataSourceContext);
  const stateHistoryRef = useRef<State<FormValuesType>[]>([]);
  const restoredHistoryRef = useRef(restoredHistory || []);
  const savedValuesRef = useRef(savedValues);
  useEffect(() => {
    restoredHistoryRef.current = restoredHistory || [];
    savedValuesRef.current = savedValues;
  }, [restoredHistory, savedValues]);

  // keep references up to date
  onInitRef.current = onInit;
  onChangeRef.current = onChange;
  onStatePathChangeRef.current = onStatePathChange;
  dataSourcecContextRef.current = dataSourceContext;

  // register for transition changes
  useEffect(() => {
    if (!stateMachine) return;
    let startState: State | undefined;
    let selectedStateNodes: StateNode[] = [];
    const onStart: SubscriptionCallback<FormValuesType> = (
      state,
      stateNode
    ) => {
      // console.log('TRANSITION: onStart', state.path);
      selectedStateNodes = [];
      // skip root stateNode when state machine starts
      if (stateMachine.stateNode !== stateNode) {
        startState = state;
      } else {
        startState = undefined;
      }
    };
    const onChange: SubscriptionCallback<FormValuesType> = (
      state,
      stateNode
    ) => {
      // console.log('TRANSITION: onChange', state.path);
      const lastStateNode = selectedStateNodes[selectedStateNodes.length - 1];
      if (lastStateNode?.path !== stateNode.path) {
        selectedStateNodes.push(stateNode);
      }
    };
    const onEnd: SubscriptionCallback<FormValuesType> = (state, stateNode) => {
      // console.log('TRANSITION: onEnd', state.path);
      // only update selected state nodes if transitioning away
      if (state.path !== startState?.path) {
        if (startState) {
          stateHistoryRef.current.push(startState);
        }
        // only display one stateNode (section) at time even for transient stateNodes
        // use the selectedStateNodes if available set during onChange vs the onEnd
        // to get the immediate next state node
        const nextStateNode = selectedStateNodes[0];
        selectedStateNodes = [nextStateNode];
        const sectionIndex =
          stateMachine.stateNode.states.indexOf(nextStateNode);
        if (stateMachine.currentState) {
          stateMachine.currentState.path = nextStateNode.path;
        }
        const sectionCount = stateMachine?.stateNode.states.length || 0;
        onStatePathChangeRef.current?.(
          nextStateNode.path,
          selectedStateNodes,
          sectionIndex,
          sectionCount,
          stateMachine
        );
      }
    };
    stateMachine.subscribe(SubscriptionType.TransitionStart, onStart);
    stateMachine.subscribe(SubscriptionType.TransitionChange, onChange);
    stateMachine.subscribe(SubscriptionType.TransitionEnd, onEnd);
    return () => {
      stateMachine.unsubscribe(SubscriptionType.TransitionStart, onStart);
      stateMachine.unsubscribe(SubscriptionType.TransitionChange, onChange);
      stateMachine.unsubscribe(SubscriptionType.TransitionEnd, onEnd);
    };
  }, [stateMachine]);

  // initial values load
  useEffect(() => {
    if (values) {
      // Keep the machine's copy in step; it is only seeded on mount.
      syncMachineFormValues(
        stateMachine?.currentState?.context?.formValues as
          Record<string, unknown> | undefined,
        values as Record<string, unknown>
      );
      setFormValues(values);
    }
  }, [values, setFormValues, stateMachine]);

  // start the state machine on mount
  useEffect(() => {
    if (!stateMachine) return;
    const stateNode = statePath
      ? stateMachine.getStateNode(statePath)
      : undefined;
    formContext.dataSourceContext = dataSourcecContextRef.current;
    if (!mountRef.current) {
      // execute this once on component mount
      mountRef.current = true;
      formContext.formValues = values || formValues;
      if (statePath && stateNode) {
        setActiveStateNodes([stateNode]);
        // set currentState so that on load it doesn't auto transition
        stateMachine.currentState = { path: statePath, context: formContext };
        // Note: Don't resume to prevent auto transitions on mount
        // stateMachine.resume({ path: statePath, context: formContext });
      } else {
        const stateNode = stateMachine.stateNode.states?.[0];
        if (stateNode) {
          // set initial currentState so that on load it doesn't auto transition
          stateMachine.currentState = {
            path: stateNode.path,
            context: formContext
          };
          setActiveStateNodes([stateNode]);
        } else {
          // default to start the state machine, which will possibly trigger
          // auto transition
          stateMachine.start(formContext);
        }
      }
      onInitRef.current?.(formContext);
    } else if (
      statePath &&
      stateNode &&
      previousStatePath.current !== statePath
    ) {
      setActiveStateNodes([stateNode]);
      // set currentState so that on load it doesn't auto transition
      stateMachine.currentState = { path: statePath, context: formContext };
      // Note: Don't resume to prevent auto transitions on mount
      // stateMachine.resume({ path: statePath, context: formContext });
    }
    previousStatePath.current = statePath;
  }, [
    stateMachine,
    formContext,
    values,
    statePath,
    formValues,
    setActiveStateNodes
  ]);
  useEffect(() => {
    if (formValues && formValues !== previousFormValues.current) {
      previousFormValues.current = formValues;
      onChangeRef.current?.(formValues);
    }
  }, [formValues]);
  useEffect(() => {
    if (!backApiRef) return;
    backApiRef.current = {
      goBack: () =>
        previousSectionPath(
          restoredHistoryRef.current,
          activeStateNodes[0]?.path
        ),
      syncToPath: path => {
        if (!stateMachine) return;
        const stateNode = path
          ? stateMachine.getStateNode(path)
          : stateMachine.stateNode.states?.[0];
        if (!stateNode) return;
        if (stateMachine.currentState?.path !== stateNode.path) {
          stateMachine.currentState = {
            path: stateNode.path,
            context: formContext
          };
        }
        if (activeStateNodes[0] !== stateNode) setActiveStateNodes([stateNode]);
      },
      hasUnsavedAnswers: () =>
        hasUnsavedAnswers(
          activeStateNodes.flatMap(node => node.meta?.elements || []),
          (formValues || {}) as Record<string, unknown>,
          savedValuesRef.current as Record<string, unknown> | undefined
        )
    };
    return () => {
      backApiRef.current = undefined;
    };
  }, [
    backApiRef,
    stateMachine,
    activeStateNodes,
    formValues,
    formContext,
    setActiveStateNodes
  ]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const progress = useMemo(() => {
    const visibleStateNodes =
      stateMachine?.stateNode.states.filter(
        stateNode =>
          !elementsActOnMount(stateNode.meta?.elements, elementPlugins)
      ) || [];
    let currentIndex = visibleStateNodes.indexOf(activeStateNodes[0] as any);
    if (currentIndex === -1) currentIndex = 0;
    return currentIndex / visibleStateNodes?.length;
  }, [stateMachine?.stateNode.states, activeStateNodes, elementPlugins]);

  const bottomTabHeight = useSafeTabBarHeight();
  return (
    <StyledDynamicFormView>
      <StyledProgressView>
        <StyledProgressContainerView>
          <ProgressBar
            trackColor={Colors.primaryPurpleLight}
            progress={progress}
          />
        </StyledProgressContainerView>
        {/* <StyledProgressText color='medium'>
            {Math.round(progress * 100)}%
          </StyledProgressText> */}
      </StyledProgressView>
      <StyledScrollView
        bottomOffset={bottomTabHeight}
        scrollEnabled={isScrollEnabled}
        onMomentumScrollEnd={event => {
          const scrollOffset = event.nativeEvent.contentOffset.y;
          setScrollPosition(scrollOffset);
        }}
        overScrollMode={
          Platform.OS === 'android'
            ? Device.platformApiLevel
              ? Device.platformApiLevel > 30
                ? 'never'
                : 'auto'
              : 'auto'
            : 'auto'
        }
        scrollIndicatorInsets={{ right: 1 }}
        keyboardShouldPersistTaps='handled'
        contentContainerStyle={{
          alignItems: 'center',
          flexGrow: 1 // Important for forms above the fold to anchor buttons to bottom
        }}
      >
        {activeStateNodes.map((stateNode, index) => (
          <FormSection
            key={stateNode.name}
            stateNode={stateNode}
            appendSectionControls={
              index === activeStateNodes.length - 1
                ? appendSectionControls
                : undefined
            }
            onBack={onBack}
            onTransition={event => {
              transition(event);
            }}
          />
        ))}
        {children}
      </StyledScrollView>
    </StyledDynamicFormView>
  );
};

export default DynamicForm;
